import React, { useState, useRef } from 'react';
import { Modal, Button, Form, Alert, Image } from 'react-bootstrap';
import { apiService } from '../services/api';

interface ScreenshotUploadModalProps {
  show: boolean;
  onHide: () => void;
  domain: string;
  onScreenshotUploaded: () => void;
}

const ScreenshotUploadModal: React.FC<ScreenshotUploadModalProps> = ({
  show,
  onHide,
  domain,
  onScreenshotUploaded,
}) => {
  const [snapshotName, setSnapshotName] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [isUploading, setIsUploading] = useState(false);
  const [alert, setAlert] = useState<{ type: 'success' | 'danger'; message: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Set default snapshot name when modal opens
  React.useEffect(() => {
    if (show && domain) {
      // Extract subdomain from domain (e.g., "test.demo.example.com" -> "test")
      const subdomain = domain.split('.')[0];
      const defaultName = `snapshot_${subdomain}_${new Date().toISOString().split('T')[0]}`;
      setSnapshotName(defaultName);
    }
  }, [show, domain]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      setSelectedFile(file);
      
      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewUrl(e.target?.result as string);
      };
      reader.readAsDataURL(file);
      setAlert(null);
    } else if (file) {
      setAlert({ type: 'danger', message: 'Lütfen bir resim dosyası seçin' });
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!selectedFile || !snapshotName.trim()) {
      setAlert({ type: 'danger', message: 'Lütfen tüm alanları doldurun ve bir dosya seçin' });
      return;
    }

    setIsUploading(true);
    setAlert(null);

    try {
      console.log('Creating snapshot:', { domain, snapshotName: snapshotName.trim(), hasFile: !!selectedFile });
      
      const result = await apiService.createSnapshot(domain, snapshotName.trim(), selectedFile);
      console.log('Snapshot creation result:', result);
      
      setAlert({ type: 'success', message: 'Snapshot başarıyla oluşturuldu!' });
      
      // Auto close after 2 seconds
      setTimeout(() => {
        onScreenshotUploaded();
        handleClose();
      }, 2000);
      
    } catch (error: any) {
      console.error('Snapshot creation error:', error);
      
      let errorMessage = 'Snapshot oluşturma başarısız oldu';
      
      if (error.code === 'ERR_NETWORK') {
        errorMessage = 'Ağ bağlantısı hatası - API\'ye erişim sağlanamıyor';
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setAlert({ 
        type: 'danger', 
        message: errorMessage
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    setSnapshotName('');
    setSelectedFile(null);
    setPreviewUrl('');
    setAlert(null);
    setIsUploading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header 
        closeButton 
        style={{
          background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
          color: 'white'
        }}
      >
        <Modal.Title>
          <i className="bi bi-camera-fill"></i> Snapshot Oluştur
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body style={{ padding: '30px' }}>
        <Alert variant="info" className="mb-3">
          <i className="bi bi-info-circle"></i>{' '}
          <strong>{domain}</strong> demo sitesi için snapshot oluşturacaksınız.
        </Alert>

        {alert && (
          <Alert variant={alert.type} className="mb-3">
            {alert.message}
          </Alert>
        )}

        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">
              <i className="bi bi-tag"></i> Snapshot Adı
            </Form.Label>
            <Form.Control
              type="text"
              value={snapshotName}
              onChange={(e) => setSnapshotName(e.target.value)}
              placeholder="Snapshot adını girin"
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">
              <i className="bi bi-image"></i> Screenshot Dosyası
            </Form.Label>
            <div 
              className="upload-area p-4 border rounded text-center"
              style={{ 
                background: '#f8f9fa', 
                border: '2px dashed #dee2e6',
                transition: 'all 0.3s ease'
              }}
            >
              <i className="bi bi-cloud-upload" style={{ fontSize: '2rem', color: '#6c757d' }}></i>
              <p className="mt-2 mb-2">Dosyayı seçmek için tıklayın</p>
              <Form.Control
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                required
                style={{ maxWidth: '300px', margin: '0 auto' }}
              />
            </div>
          </Form.Group>

          {previewUrl && (
            <div className="text-center mb-3">
              <Image
                src={previewUrl}
                alt="Preview"
                style={{
                  maxWidth: '200px',
                  maxHeight: '200px',
                  borderRadius: '8px',
                  border: '1px solid #dee2e6'
                }}
              />
            </div>
          )}

          <Button
            type="submit"
            variant="primary"
            disabled={isUploading || !selectedFile}
            className="w-100"
            style={{
              background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
              border: 'none',
              padding: '12px'
            }}
          >
            {isUploading ? (
              <>
                <i className="bi bi-hourglass-split"></i> Yükleniyor...
              </>
            ) : (
              <>
                <i className="bi bi-camera2"></i> Snapshot Oluştur
              </>
            )}
          </Button>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default ScreenshotUploadModal;